// src/pages/dashboard/branch/menu-management/cross-selling/CrossSellingPage.tsx
import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { FiSearch, FiX, FiLink, FiFilter } from 'react-icons/fi';
import { motion, AnimatePresence } from 'framer-motion';
import { crossSellingService } from '../../../../../services/crossSellingService';
import { CrossSellingRelation, CrossSellingMenuItem } from '../../../../../types/crossSelling';

const CrossSellingPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { branchId } = useParams<{ branchId: string }>();
  const isRTL = i18n.language === 'ar';

  const [itemsMap, setItemsMap] = useState<Map<string, CrossSellingMenuItem>>(new Map());
  const [relations, setRelations] = useState<CrossSellingRelation[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState<'all' | 'manual' | 'auto'>('all');

  // Load relations and items on mount or when filter changes
  useEffect(() => {
    if (branchId) {
      loadData();
    }
  }, [branchId, filterType]);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Convert filter type to is_manual param
      const isManualParam = filterType === 'all' ? undefined : filterType === 'manual';
      
      const [relationsData, itemsData] = await Promise.all([
        crossSellingService.getAllCrossSelling(branchId!, isManualParam),
        crossSellingService.getBranchMenuItems(branchId!),
      ]);
      
      setRelations(relationsData);
      setItemsMap(new Map(itemsData.map(item => [item.id, item])));
    } catch (error) {
      console.error('Error loading cross-selling data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getDisplayName = (itemId: string, fallbackName: string) => {
    const item = itemsMap.get(itemId);
    if (!item) return fallbackName;
    if (isRTL && item.ar?.name) return item.ar.name;
    return item.name;
  };

  const getItemImage = (itemId: string, fallbackImage?: string) => {
    const item = itemsMap.get(itemId);
    return item?.image_url || fallbackImage;
  };

  // Filter relations based on search
  const filteredRelations = useMemo(() => {
    if (!searchQuery.trim()) return relations;

    const query = searchQuery.toLowerCase();
    return relations.filter((relation) => {
      const triggerName = getDisplayName(relation.trigger_item_id, relation.trigger_item_name).toLowerCase();
      const suggestedName = getDisplayName(relation.suggested_item_id, relation.suggested_item_name).toLowerCase();
      return triggerName.includes(query) || suggestedName.includes(query);
    });
  }, [relations, searchQuery, itemsMap, isRTL]);



  // Loading state
  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gradient-to-r from-gray-200 to-gray-300 dark:from-gray-700 dark:to-gray-600 rounded w-1/4" />
          <div className="h-12 bg-gray-200 dark:bg-gray-700 rounded" />
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <div
                key={i}
                className="h-20 bg-gray-200 dark:bg-gray-700 rounded-lg"
              />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <motion.div
      className="p-6"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      dir={isRTL ? 'rtl' : 'ltr'}
    >
      {/* Header Section */}
      <div className="mb-8">
        <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4">
          <div>
            <h1 className="text-3xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
              {t('menuItems.crossSelling', 'Cross-Selling')}
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mt-1">
              {t('menuManagement.crossSelling.subtitle', 'Manage manual item recommendations')}
            </p>
            <div className="flex items-center gap-4 mt-3 text-sm text-gray-500">
              <span className="flex items-center gap-1">
                <div className="w-2 h-2 bg-green-500 rounded-full" />
                {relations.length} {t('common.total')}
              </span>
              <span className="flex items-center gap-1">
                <div className="w-2 h-2 bg-purple-500 rounded-full" />
                {filteredRelations.length} {t('common.showing')}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Search & Filter Section */}
      <div className="mb-6 bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-4">
        <div className="flex flex-col sm:flex-row gap-3">
          {/* Search Input */}
          <div className="relative flex-1">
            <FiSearch className="absolute start-3.5 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder={t('menuItems.searchItems', 'Search items...')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full ps-10 pe-10 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute end-3 top-1/2 transform -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-full transition-colors"
              >
                <FiX className="w-4 h-4" />
              </button>
            )}
          </div>

          {/* Filter Dropdown */}
          <div className="relative">
            <FiFilter className="absolute start-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value as 'all' | 'manual' | 'auto')}
              className="ps-9 pe-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white appearance-none cursor-pointer min-w-[140px]"
            >
              <option value="all">{t('common.all', 'All')}</option>
              <option value="manual">{t('menuManagement.crossSelling.manual', 'Manual')}</option>
              <option value="auto">{t('menuManagement.crossSelling.auto', 'Auto')}</option>
            </select>
          </div>
        </div>
      </div>

      {/* Content Section */}
      {filteredRelations.length === 0 ? (
        <div className="text-center py-16 bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
          <div className="w-20 h-20 mx-auto mb-6 bg-purple-100 dark:bg-purple-900/40 rounded-full flex items-center justify-center">
            <FiLink className="w-10 h-10 text-purple-600 dark:text-purple-400" />
          </div>
          <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">
            {relations.length === 0
              ? t('menuManagement.crossSelling.noRelations', 'No Cross-Selling Configured')
              : t('common.noResults', 'No results found')}
          </h3>
          <p className="text-gray-500 dark:text-gray-400 mb-6 max-w-md mx-auto">
            {relations.length === 0
              ? t('menuManagement.crossSelling.noRelationsDescription', 'Add cross-selling recommendations from the menu item edit modal')
              : t('common.noResultsDescription', 'Try adjusting your search')}
          </p>
        </div>
      ) : (
        <motion.div
          className="space-y-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.3 }}
        >
          <AnimatePresence mode="popLayout">
            {filteredRelations.map((relation) => {
               const triggerName = getDisplayName(relation.trigger_item_id, relation.trigger_item_name);
               const triggerImage = getItemImage(relation.trigger_item_id, relation.trigger_item_image);
               const suggestedName = getDisplayName(relation.suggested_item_id, relation.suggested_item_name);
               const suggestedImage = getItemImage(relation.suggested_item_id, relation.suggested_item_image);

               return (
              <motion.div
                key={relation.id}
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                transition={{ duration: 0.2 }}
                className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-4 hover:shadow-md transition-shadow"
              >


                {/* Mobile Layout (Vertical Stacked - Option D) */}
                <div className="flex md:hidden flex-col items-center gap-3">
                  {/* First Item */}
                  <div className="flex items-center gap-3 w-full p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                    {triggerImage ? (
                      <img src={triggerImage} alt={triggerName} className="w-14 h-14 rounded-lg object-cover flex-shrink-0" />
                    ) : (
                      <div className="w-14 h-14 rounded-lg bg-gray-200 dark:bg-gray-600 flex items-center justify-center flex-shrink-0">
                        <span className="text-gray-400 text-xs">{t('menuItems.noImage')}</span>
                      </div>
                    )}
                    <p className="font-medium text-gray-900 dark:text-white truncate flex-1">{triggerName}</p>
                  </div>

                  {/* Bidirectional Arrow */}
                  <div className="flex items-center justify-center text-purple-500">
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
                    </svg>
                  </div>

                  {/* Second Item */}
                  <div className="flex items-center gap-3 w-full p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                    {suggestedImage ? (
                      <img src={suggestedImage} alt={suggestedName} className="w-14 h-14 rounded-lg object-cover flex-shrink-0" />
                    ) : (
                      <div className="w-14 h-14 rounded-lg bg-gray-200 dark:bg-gray-600 flex items-center justify-center flex-shrink-0">
                        <span className="text-gray-400 text-xs">{t('menuItems.noImage')}</span>
                      </div>
                    )}
                    <p className="font-medium text-gray-900 dark:text-white truncate flex-1">{suggestedName}</p>
                  </div>
                </div>

                {/* Desktop Layout (Horizontal Pill - Option B) */}
                <div className="hidden md:flex items-center gap-4">
                  {/* First Item Pill */}
                  <div className="flex items-center gap-3 flex-1 min-w-0 p-2 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                    {triggerImage ? (
                      <img src={triggerImage} alt={triggerName} className="w-10 h-10 rounded-lg object-cover flex-shrink-0" />
                    ) : (
                      <div className="w-10 h-10 rounded-lg bg-gray-200 dark:bg-gray-600 flex items-center justify-center flex-shrink-0">
                        <span className="text-gray-400 text-xs">{t('menuItems.noImage')}</span>
                      </div>
                    )}
                    <p className="font-medium text-gray-900 dark:text-white truncate">{triggerName}</p>
                  </div>

                  {/* Bidirectional Arrow */}
                  <div className="flex-shrink-0 px-2 text-purple-500">
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                    </svg>
                  </div>

                  {/* Second Item Pill */}
                  <div className="flex items-center gap-3 flex-1 min-w-0 p-2 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                    {suggestedImage ? (
                      <img src={suggestedImage} alt={suggestedName} className="w-10 h-10 rounded-lg object-cover flex-shrink-0" />
                    ) : (
                      <div className="w-10 h-10 rounded-lg bg-gray-200 dark:bg-gray-600 flex items-center justify-center flex-shrink-0">
                        <span className="text-gray-400 text-xs">{t('menuItems.noImage')}</span>
                      </div>
                    )}
                    <p className="font-medium text-gray-900 dark:text-white truncate">{suggestedName}</p>
                  </div>


                </div>
              </motion.div>
            )})}
          </AnimatePresence>
        </motion.div>
      )}


    </motion.div>
  );
};

export default CrossSellingPage;
